import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    FormControlLabel,
    Switch,
    Stack,
    Alert,
    Divider,
    Chip
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, getMaintenanceMode, toggleMaintenanceMode, retryFn } from "@lib/apis";
import { useSnackbar } from "notistack";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { Save, Warning } from "@mui/icons-material";
import { format } from "date-fns";

interface MaintenanceModeData {
    is_enabled: boolean;
    message: string;
    enabled_at: string | null;
    enabled_by_email: string | null;
    last_updated: string;
}

export default function AdminMaintenanceMode() {
    const { enqueueSnackbar } = useSnackbar();
    const [isEnabled, setIsEnabled] = useState(false);
    const [message, setMessage] = useState("");
    const [pageData, setPageData] = useState<MaintenanceModeData | null>(null);

    // Fetch the current maintenance mode status
    const pageDataQuery = useQuery({
        queryKey: ["adminMaintenanceMode"],
        queryFn: () => getMaintenanceMode(),
        refetchOnWindowFocus: false,
        retry: retryFn,
        gcTime: 0,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            const data = pageDataQuery.data.data as MaintenanceModeData;
            setPageData(data);
            setIsEnabled(data.is_enabled);
            setMessage(data.message || "");
        }
    }, [pageDataQuery.data]);

    // Mutation to toggle maintenance mode
    const toggleMaintenanceModeMutation = useMutation({
        mutationFn: () => toggleMaintenanceMode(isEnabled, message),
        onSuccess: (response) => {
            const data = response.data.data as MaintenanceModeData;
            setPageData(data);
            enqueueSnackbar(
                `Maintenance mode ${data.is_enabled ? 'enabled' : 'disabled'} successfully`,
                { variant: "success" }
            );
            pageDataQuery.refetch();
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(
                error.data?.message || "Failed to update maintenance mode",
                { variant: "error" }
            );
        },
        retry: retryFn,
    });

    const handleSave = () => {
        toggleMaintenanceModeMutation.mutate();
    };

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (pageDataQuery.isLoading) {
        return <PageLoading />;
    }

    if (pageDataQuery.error) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />;
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Maintenance Mode
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Control the maintenance mode for the application. When enabled, users will see a maintenance message.
            </Typography>

            <Card>
                <CardContent>
                    <Stack spacing={3}>
                        {/* Current Status Display */}
                        {pageData && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Current Status
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                    <Chip
                                        label={pageData.is_enabled ? "ENABLED" : "DISABLED"}
                                        color={pageData.is_enabled ? "error" : "success"}
                                        size="small"
                                    />
                                    {pageData.is_enabled && pageData.enabled_by_email && (
                                        <Typography variant="caption" color="text.secondary">
                                            by {pageData.enabled_by_email}
                                        </Typography>
                                    )}
                                </Stack>
                                {pageData.enabled_at && pageData.is_enabled && (
                                    <Typography variant="caption" color="text.secondary">
                                        Enabled at: {format(new Date(pageData.enabled_at), "PPpp")}
                                    </Typography>
                                )}
                                {pageData.last_updated && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Last updated: {format(new Date(pageData.last_updated), "PPpp")}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Divider />

                        {/* Toggle Switch */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isEnabled}
                                        onChange={(e) => setIsEnabled(e.target.checked)}
                                        color={isEnabled ? "error" : "primary"}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1">
                                            {isEnabled ? "Maintenance Mode Enabled" : "Maintenance Mode Disabled"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {isEnabled
                                                ? "Users will see the maintenance message and cannot access the application"
                                                : "Application is running normally"}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>

                        {/* Warning Alert */}
                        {isEnabled && (
                            <Alert severity="warning" icon={<Warning />}>
                                Enabling maintenance mode will prevent all users from accessing the application.
                                Only admin users can access the system during maintenance.
                            </Alert>
                        )}

                        {/* Message TextField */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Maintenance Message
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Enter a message to display to users during maintenance..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={!isEnabled}
                                helperText={
                                    isEnabled
                                        ? "This message will be displayed to users when maintenance mode is active"
                                        : "Enable maintenance mode to set a message"
                                }
                            />
                        </Box>

                        {/* Save Button */}
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color={isEnabled ? "error" : "primary"}
                                startIcon={<Save />}
                                onClick={handleSave}
                                disabled={toggleMaintenanceModeMutation.isPending}
                            >
                                {toggleMaintenanceModeMutation.isPending ? "Saving..." : "Save Changes"}
                            </Button>
                        </Box>

                        {/* Current Message Display */}
                        {pageData && pageData.is_enabled && pageData.message && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Current Active Message
                                    </Typography>
                                    <Alert severity="info">
                                        {pageData.message}
                                    </Alert>
                                </Box>
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
